import { ArrowLeft, ChevronDown, ChevronRight, Settings2 } from 'lucide-react';
import { useState } from 'react';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Form, Link, useLoaderData } from 'react-router';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/components/ui/card';
import { Label } from '~/components/ui/label';
import { getAllSpecs, getSpecBySlug } from '~/lib/db/queries/specs';
import type { FeatureDefinition } from '~/lib/feature-modules/types';
import { CATEGORY_LABELS } from '~/lib/utils';

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
  const a = loaderData?.specA?.name;
  const b = loaderData?.specB?.name;
  const title =
    a && b ? `Compare: ${a} vs ${b} — Specly` : 'Compare Specs — Specly';
  return [{ title }];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const slugA = url.searchParams.get('a') ?? '';
  const slugB = url.searchParams.get('b') ?? '';

  const [specA, specB, allSpecs] = await Promise.all([
    slugA ? getSpecBySlug(slugA) : null,
    slugB ? getSpecBySlug(slugB) : null,
    getAllSpecs(),
  ]);

  return { specA, specB, slugA, slugB, allSpecs };
}

function complexityScore(spec: {
  specFeatures: { feature: { complexity: string } }[];
}) {
  return spec.specFeatures.reduce((sum, sf) => {
    const w =
      sf.feature.complexity === 'high'
        ? 3
        : sf.feature.complexity === 'medium'
          ? 2
          : 1;
    return sum + w;
  }, 0);
}

export default function ComparePage() {
  const { specA, specB, slugA, slugB, allSpecs } =
    useLoaderData<typeof loader>();

  const featureSetA = new Set(
    specA?.specFeatures.map((sf) => sf.feature.slug) ?? [],
  );
  const featureSetB = new Set(
    specB?.specFeatures.map((sf) => sf.feature.slug) ?? [],
  );
  const allSlugs = new Set([...featureSetA, ...featureSetB]);

  const shared = [...allSlugs].filter(
    (s) => featureSetA.has(s) && featureSetB.has(s),
  );
  const onlyA = [...allSlugs].filter(
    (s) => featureSetA.has(s) && !featureSetB.has(s),
  );
  const onlyB = [...allSlugs].filter(
    (s) => !featureSetA.has(s) && featureSetB.has(s),
  );

  const featureNameMap: Record<string, string> = {};
  const featureComplexityMap: Record<string, string> = {};
  for (const sf of [
    ...(specA?.specFeatures ?? []),
    ...(specB?.specFeatures ?? []),
  ]) {
    featureNameMap[sf.feature.slug] = sf.feature.name;
    featureComplexityMap[sf.feature.slug] = sf.feature.complexity;
  }

  const scoreA = specA ? complexityScore(specA as any) : 0;
  const scoreB = specB ? complexityScore(specB as any) : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="size-4" />
        All specs
      </Link>

      <h1 className="text-2xl font-bold mb-6">Compare Specs</h1>

      {/* Spec picker */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div className="flex flex-col gap-1.5">
          <Label>Spec A</Label>
          <Form method="get" className="flex gap-2">
            {slugB && <input type="hidden" name="b" value={slugB} />}
            <select
              name="a"
              defaultValue={slugA}
              className="flex-1 h-8 px-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              onChange={(e) => {
                const form = e.target.form;
                if (form) form.requestSubmit();
              }}
            >
              <option value="">Select a spec...</option>
              {allSpecs.map((s) => (
                <option key={s.id} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          </Form>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Spec B</Label>
          <Form method="get" className="flex gap-2">
            {slugA && <input type="hidden" name="a" value={slugA} />}
            <select
              name="b"
              defaultValue={slugB}
              className="flex-1 h-8 px-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              onChange={(e) => {
                const form = e.target.form;
                if (form) form.requestSubmit();
              }}
            >
              <option value="">Select a spec...</option>
              {allSpecs.map((s) => (
                <option key={s.id} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          </Form>
        </div>
      </div>

      {specA && specB ? (
        <>
          {/* Header row */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {[specA, specB].map((spec) => (
              <Card key={spec.id}>
                <CardHeader>
                  <div className="text-xs text-muted-foreground mb-1">
                    {CATEGORY_LABELS[spec.category] ?? spec.category}
                  </div>
                  <CardTitle>{spec.name}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {spec.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    size="sm"
                    render={<Link to={`/specs/${spec.slug}/build`} />}
                  >
                    <Settings2 data-icon="inline-start" />
                    Build {spec.name}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Complexity comparison */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-sm">Complexity Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { spec: specA, score: scoreA },
                  { spec: specB, score: scoreB },
                ].map(({ spec, score }) => (
                  <div key={spec.id}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-muted-foreground">
                        {spec.name}
                      </span>
                      <span className="text-sm font-semibold">{score} pts</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, (score / Math.max(scoreA, scoreB, 1)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Feature overlap */}
          <div className="flex flex-col gap-4">
            {shared.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">
                    Shared features
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      ({shared.length})
                    </span>
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Click a row to see how each spec configures it.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col divide-y divide-border/60">
                    {shared.map((slug) => {
                      const sfA = specA.specFeatures.find(
                        (sf) => sf.feature.slug === slug,
                      );
                      const sfB = specB.specFeatures.find(
                        (sf) => sf.feature.slug === slug,
                      );
                      if (!sfA || !sfB) return null;
                      return (
                        <SharedFeatureRow
                          key={slug}
                          slug={slug}
                          name={featureNameMap[slug] ?? slug}
                          complexity={featureComplexityMap[slug]}
                          aLabel={specA.name}
                          bLabel={specB.name}
                          aRequired={sfA.isRequired}
                          bRequired={sfB.isRequired}
                          aPreset={sfA.defaultPreset ?? null}
                          bPreset={sfB.defaultPreset ?? null}
                          aDefinition={sfA.definition}
                          bDefinition={sfB.definition}
                        />
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {(onlyA.length > 0 || onlyB.length > 0) && (
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">
                      Only in {specA.name}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        ({onlyA.length})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {onlyA.length === 0 ? (
                      <p className="text-xs text-muted-foreground">None</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {onlyA.map((slug) => (
                          <Badge key={slug} variant="outline">
                            {featureNameMap[slug] ?? slug}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">
                      Only in {specB.name}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        ({onlyB.length})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {onlyB.length === 0 ? (
                      <p className="text-xs text-muted-foreground">None</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {onlyB.map((slug) => (
                          <Badge key={slug} variant="outline">
                            {featureNameMap[slug] ?? slug}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="py-10 border-2 border-dashed border-border rounded-xl">
          <div className="text-center mb-6 text-muted-foreground">
            <p className="text-lg font-medium mb-1">
              Select two specs to compare
            </p>
            <p className="text-sm">Choose specs from the dropdowns above</p>
          </div>
          <CompareSuggestions allSpecs={allSpecs} />
        </div>
      )}
    </div>
  );
}

function SharedFeatureRow({
  slug: _slug,
  name,
  complexity,
  aLabel,
  bLabel,
  aRequired,
  bRequired,
  aPreset,
  bPreset,
  aDefinition,
  bDefinition,
}: {
  slug: string;
  name: string;
  complexity: string | undefined;
  aLabel: string;
  bLabel: string;
  aRequired: boolean;
  bRequired: boolean;
  aPreset: string | null;
  bPreset: string | null;
  aDefinition: FeatureDefinition;
  bDefinition: FeatureDefinition;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-muted/40 rounded px-2 -mx-2"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="size-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 text-muted-foreground" />
          )}
          <span className="text-sm font-medium">{name}</span>
          {complexity && (
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {complexity}
            </span>
          )}
        </span>
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          {aRequired && (
            <Badge variant="secondary" className="text-[10px]">
              req in A
            </Badge>
          )}
          {bRequired && (
            <Badge variant="secondary" className="text-[10px]">
              req in B
            </Badge>
          )}
        </span>
      </button>
      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3 pt-1 pl-6">
          <FeatureDetailColumn
            label={aLabel}
            preset={aPreset}
            definition={aDefinition}
          />
          <FeatureDetailColumn
            label={bLabel}
            preset={bPreset}
            definition={bDefinition}
          />
        </div>
      )}
    </div>
  );
}

function FeatureDetailColumn({
  label,
  preset,
  definition,
}: {
  label: string;
  preset: string | null;
  definition: FeatureDefinition;
}) {
  const optionNames = definition.optionGroups.flatMap((g) =>
    g.options
      .filter((o) =>
        preset &&
        (preset === 'lightweight' ||
          preset === 'standard' ||
          preset === 'enterprise')
          ? o.presets.includes(preset)
          : o.presets.includes('standard'),
      )
      .map((o) => o.label),
  );
  const topTradeoffs = definition.tradeoffs.slice(0, 3);

  return (
    <div className="rounded-lg border border-border/60 p-3 bg-muted/20">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold">{label}</p>
        {preset && (
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-background border border-border text-muted-foreground">
            {preset}
          </span>
        )}
      </div>
      {optionNames.length > 0 ? (
        <div className="flex flex-wrap gap-1 mb-2">
          {optionNames.map((n) => (
            <span
              key={n}
              className="text-[11px] px-1.5 py-0.5 rounded bg-background border border-border"
            >
              {n}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground mb-2">No preset options.</p>
      )}
      {topTradeoffs.length > 0 && (
        <ul className="flex flex-col gap-1">
          {topTradeoffs.map((t, i) => (
            <li key={i} className="text-[11px] text-muted-foreground">
              <span className="font-medium">{t.dimension}</span>{' '}
              <span
                className={
                  t.direction === 'increases'
                    ? 'text-destructive'
                    : 'text-emerald-600'
                }
              >
                {t.direction === 'increases' ? '↑' : '↓'}
              </span>{' '}
              {t.impact}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const COMPARE_SUGGESTIONS: { title: string; a: string; b: string }[] = [
  { title: 'Slack vs Project Mgmt', a: 'slack-like', b: 'project-mgmt' },
  { title: 'CRM vs Marketplace', a: 'crm', b: 'marketplace' },
  { title: 'SaaS starter vs AI SaaS', a: 'saas-starter', b: 'ai-saas' },
];

function CompareSuggestions({
  allSpecs,
}: {
  allSpecs: { slug: string; name: string }[];
}) {
  const available = new Set(allSpecs.map((s) => s.slug));
  const suggestions = COMPARE_SUGGESTIONS.filter(
    (s) => available.has(s.a) && available.has(s.b),
  );
  if (suggestions.length === 0) return null;
  return (
    <div className="max-w-2xl mx-auto px-4">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 text-center">
        Or try a popular pairing
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {suggestions.map((s) => (
          <Link
            key={s.title}
            to={`/compare?a=${s.a}&b=${s.b}`}
            className="text-left px-3 py-3 rounded-xl border border-border bg-background hover:border-primary/40 hover:bg-primary/5 transition-colors"
          >
            <div className="text-sm font-semibold">{s.title}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Compare →
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
